// src/components/InboundModal.tsx
import React, { useState, useCallback } from 'react';
import {
  Modal, Form, Input, Select, InputNumber, Button,
  Table, Space, Divider, Tag, message, Row, Col,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { STORE_NAMES } from '../utils/db';
import { apiCreateInbound, type InboundLinePayload } from '../services/inboundAPI';
import AddProductFromInboundModal from './AddProductFromInboundModal';

const { Option } = Select;

const CATEGORIES = [
  { value: STORE_NAMES.sparkPlugs,     label: 'Свечи зажигания' },
  { value: STORE_NAMES.coils,          label: 'Катушки зажигания' },
  { value: STORE_NAMES.sensors,        label: 'Датчики' },
  { value: STORE_NAMES.steeringClocks, label: 'Рулевые шлейфы' },
  { value: STORE_NAMES.wires,          label: 'Наконечники и провода' },
];

type LineForm = {
  category: string;
  productId?: string;
  brand?: string;
  partNumber?: string;
  model?: string;
  sku?: string;
  qty: number;
  unitCost: number;
  quality: 'original' | 'copy';
};

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

const InboundModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [headerForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [lines, setLines] = useState<LineForm[]>([]);
  const [searchResults, setSearchResults] = useState<Record<number, any[]>>({});
  const [searching, setSearching] = useState<Record<number, boolean>>({});
  // Очередь индексов строк без выбранного продукта → поочерёдное создание товаров
  const [productQueue, setProductQueue]       = useState<number[]>([]);
  const [addProductOpen, setAddProductOpen]   = useState(false);
  const [createdProducts, setCreatedProducts] = useState<Record<number, any>>({});

  const handleClose = () => {
    headerForm.resetFields();
    setLines([]);
    setSearchResults({});
    onClose();
  };

  const addLine = () => setLines(prev => [...prev, {
    category: STORE_NAMES.sparkPlugs,
    qty: 1, unitCost: 0, quality: 'original',
  }]);

  const removeLine = (idx: number) => {
    setLines(prev => prev.filter((_, i) => i !== idx));
    setSearchResults(prev => { const n = { ...prev }; delete n[idx]; return n; });
  };

  const updateLine = (idx: number, patch: Partial<LineForm>) =>
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const searchProduct = useCallback(async (idx: number, query: string, category: string) => {
    if (!query.trim()) return;
    setSearching(prev => ({ ...prev, [idx]: true }));
    try {
      const apiBase = process.env.REACT_APP_API_URL ?? 'http://localhost:5000/api';
      // Find the API category name(s) for this storeName
      const { SYNC_CATEGORIES } = await import('../services/productSync');
      const cats = SYNC_CATEGORIES.filter(c => c.storeName === category);
      const q = query.trim().toLowerCase();
      const found: any[] = [];
      for (const cat of cats) {
        const res = await fetch(`${apiBase}/product/${cat.apiName}`);
        if (!res.ok) continue;
        const data: any[] = await res.json();
        const mapped = data.map(row => cat.mapper(row as any));
        const matches = mapped.filter((it: any) =>
          (it.sku && String(it.sku).toLowerCase().includes(q)) ||
          (it.partNumber && String(it.partNumber).toLowerCase().includes(q)) ||
          (it.model && String(it.model).toLowerCase().includes(q)) ||
          (it.brand && String(it.brand).toLowerCase().includes(q))
        );
        found.push(...matches);
        if (found.length >= 8) break;
      }
      setSearchResults(prev => ({ ...prev, [idx]: found.slice(0, 8) }));
    } finally {
      setSearching(prev => ({ ...prev, [idx]: false }));
    }
  }, []);

  const selectProduct = (idx: number, product: any) => {
    updateLine(idx, {
      productId: product.id,
      brand: product.brand ?? '',
      partNumber: product.partNumber ?? '',
      model: product.model ?? '',
      sku: product.sku ?? '',
    });
    setSearchResults(prev => ({ ...prev, [idx]: [] }));
  };

  // Строит payload из текущих lines, подставляя данные только что созданных продуктов
  const buildPayload = useCallback((extra: Record<number, any> = {}) => {
    const header = headerForm.getFieldsValue();
    return {
      id:       crypto.randomUUID(),
      supplier: header.supplier ?? '',
      status:   header.status   ?? 'ready',
      lines: lines.map((l, i) => {
        const created = extra[i];
        // Товар в MySQL — по нему сервер прибавляет остаток (штрихкод есть не у всех
        // карточек). У созданных только в браузере id не числовой — их не передаём.
        const pid = Number(created?.id ?? l.productId);
        return {
          product_id: Number.isInteger(pid) && pid > 0 ? pid : undefined,
          sku:        created?.sku        ?? l.sku        ?? '',
          brand:      created?.brand      ?? l.brand      ?? '',
          partNumber: created?.partNumber ?? l.partNumber ?? '',
          model:      created?.model      ?? l.model      ?? '',
          category:   l.category,
          qty:        l.qty,
          unitCost:   l.unitCost,
          totalCost:  l.qty * l.unitCost,
          quality:    l.quality,
        } as InboundLinePayload;
      }),
    };
  }, [lines, headerForm]);

  const doSave = useCallback(async (extra: Record<number, any> = {}) => {
    setSaving(true);
    try {
      await apiCreateInbound(buildPayload(extra));
      message.success('Поступление сохранено');
      handleClose();
      onCreated();
    } catch (e: any) {
      message.error(e.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  }, [buildPayload, handleClose, onCreated]);

  const handleSave = async () => {
    try { await headerForm.validateFields(); } catch { return; }
    if (lines.length === 0) { message.warning('Добавьте хотя бы одну позицию'); return; }

    // Строки без выбранного продукта из поиска → создать поочерёдно
    const unmatched = lines.reduce<number[]>((acc, l, i) => {
      if (!l.productId) acc.push(i);
      return acc;
    }, []);

    if (unmatched.length > 0) {
      setCreatedProducts({});
      setProductQueue(unmatched);
      setAddProductOpen(true);
      return;
    }

    await doSave();
  };

  // Вызывается после того как пользователь добавил очередной новый товар
  const handleProductCreated = useCallback((item: any) => {
    const currentIdx = productQueue[0];
    const newCreated = { ...createdProducts, [currentIdx]: item };
    const remaining  = productQueue.slice(1);

    setCreatedProducts(newCreated);
    setProductQueue(remaining);

    if (remaining.length === 0) {
      setAddProductOpen(false);
      doSave(newCreated); // передаём напрямую — не ждём async обновления state
    }
  }, [productQueue, createdProducts, doSave]);

  const grandTotal = lines.reduce((s, l) => s + (l.qty || 0) * (l.unitCost || 0), 0);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Новое поступление"
      width={780}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Итого рядом с кнопками */}
          <Tag color="blue" style={{ fontSize: 14, padding: '5px 14px', margin: 0 }}>
            Итого: {grandTotal.toLocaleString('ru-RU')} ֏
          </Tag>
          <Space>
            <Button onClick={handleClose}>Отмена</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>Сохранить</Button>
          </Space>
        </div>
      }
      destroyOnHidden
    >
      {/* Шапка поступления */}
      <Form form={headerForm} layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item name="supplier" label="Поставщик" style={{ flex: 1 }}>
          <Input placeholder="Название поставщика" />
        </Form.Item>
        <Form.Item name="status" label="Статус" initialValue="ready">
          <Select style={{ width: 150 }}>
            <Option value="ready">Получено</Option>
            <Option value="processing">В обработке</Option>
            <Option value="in_transit">В пути</Option>
          </Select>
        </Form.Item>
      </Form>

      <Divider orientation="left" style={{ margin: '8px 0 12px' }}>Позиции</Divider>

      {lines.map((line, idx) => (
        <div key={idx} style={{ background: '#fafafa', border: '1px solid #e6e6e3', borderRadius: 8, padding: '12px 44px 12px 14px', marginBottom: 10, position: 'relative' }}>

          {/* Кнопка удаления — в правом верхнем углу */}
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeLine(idx)}
            style={{ position: 'absolute', top: 12, right: 8 }}
          />

          {/* Строка 1: Поиск товара (главное действие — первым) */}
          <Row gutter={12} style={{ marginBottom: 10 }}>
            <Col span={24}>
              <Input.Search
                placeholder="SKU / Номер детали / Модель"
                loading={searching[idx]}
                onSearch={q => searchProduct(idx, q, line.category)}
                allowClear
              />
            </Col>
          </Row>

          {/* Строка 2: Категория + Тип */}
          <Row gutter={12} style={{ marginBottom: 10 }}>
            <Col span={14}>
              <Form.Item label="Категория" style={{ margin: 0 }}>
                <Select
                  value={line.category}
                  style={{ width: '100%' }}
                  onChange={v => updateLine(idx, { category: v, brand: '', partNumber: '', model: '', sku: '', productId: undefined })}
                >
                  {CATEGORIES.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item label="Тип" style={{ margin: 0 }}>
                <Select
                  value={line.quality}
                  style={{ width: '100%' }}
                  onChange={v => updateLine(idx, { quality: v })}
                >
                  <Option value="original">Оригинал</Option>
                  <Option value="copy">Копия</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Строка 3: Кол-во + Цена закупки + Сумма отдельно */}
          <Row gutter={12} align="middle">
            <Col span={8}>
              <Form.Item label="Кол-во" style={{ margin: 0 }}>
                <InputNumber
                  min={1} value={line.qty} style={{ width: '100%' }}
                  onChange={v => updateLine(idx, { qty: v ?? 1 })}
                />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item label="Цена закупки" style={{ margin: 0 }}>
                <InputNumber
                  min={0} value={line.unitCost} style={{ width: '100%' }}
                  formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  onChange={v => updateLine(idx, { unitCost: v ?? 0 })}
                />
              </Form.Item>
            </Col>
            {/* Сумма — отдельно от полей ввода, справа */}
            <Col span={6} style={{ textAlign: 'right' }}>
              <span style={{ color: '#7a7a7a', fontSize: 12 }}>Сумма</span>
              <div style={{ fontWeight: 700, color: '#389e0d', fontSize: 15 }}>
                {((line.qty || 0) * (line.unitCost || 0)).toLocaleString('ru-RU')} ֏
              </div>
            </Col>
          </Row>

          {/* Результаты поиска */}
          {(searchResults[idx] ?? []).length > 0 && (
            <Table
              size="small"
              style={{ marginTop: 10 }}
              dataSource={searchResults[idx]}
              rowKey="id"
              pagination={false}
              onRow={record => ({ onClick: () => selectProduct(idx, record), style: { cursor: 'pointer' } })}
              columns={[
                { title: 'SKU',    dataIndex: 'sku',        width: 70 },
                { title: 'Бренд',  dataIndex: 'brand',      width: 90 },
                { title: 'Номер',  dataIndex: 'partNumber', width: 120 },
                { title: 'Модель', dataIndex: 'model',      ellipsis: true },
              ]}
            />
          )}

          {/* Выбранный товар */}
          {line.brand && (
            <div style={{ marginTop: 8, fontSize: 13, color: '#555' }}>
              ✓ <b>{line.brand}</b> {line.partNumber}
              {line.model && ` — ${line.model}`}
              {line.sku && <Tag style={{ marginLeft: 6 }}>SKU: {line.sku}</Tag>}
            </div>
          )}
        </div>
      ))}

      <Button
        icon={<PlusOutlined />}
        onClick={addLine}
        style={{ width: '100%', borderStyle: 'dashed', marginTop: 4 }}
      >
        Добавить позицию
      </Button>

      <AddProductFromInboundModal
        open={addProductOpen}
        category={lines[productQueue[0]]?.category ?? STORE_NAMES.sparkPlugs}
        onCreated={handleProductCreated}
        onClose={() => { setAddProductOpen(false); setProductQueue([]); setCreatedProducts({}); }}
        queuePosition={productQueue.length > 0 ? (Object.keys(createdProducts).length + 1) : 1}
        queueTotal={productQueue.length + Object.keys(createdProducts).length}
      />
    </Modal>
  );
};

export default InboundModal;
